import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { appName } from "../../../../app.json";
import NewCarePlan from "./NewCarePlan";
import CarePlanList from "./CarePlanList";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { LIST, CREATE, CONFIRM_PLAN, TERMINATE_PLAN, REPLAN } from "../../../../queries/care-plan.query";
import LoadingSpinner from "../../../../partials/loading";
import { ReadData } from "../../../../providers/Patient.Provider";
import FabButton from "../../../../partials/Buttons/FabButton";
import { toast } from "react-toastify";
import { CleanMessage } from "../../../../util/app";
import PlanDetail from "./PlanDetail";
import ReviewPlan from "./ReviewPlan";
import { EventEmitter } from "../../../../event/EventEmitter";
import { EVENT_TARGET, ACTION_TYPE } from "../../../../event/model";
import { sendNotification } from "../../../../sockette";

const CarePlan = ({ match }) => {
    const { id } = match.params;
    const [newForm, setNewForm] = useState(false);
    const [item, setItem] = useState(null);
    const [review, setReview] = useState(false);

    // local data
    const patient = ReadData();
    const title = "Patient Care Plan";

    // query
    const { loading, data, refetch } = useQuery(LIST, {
        variables: {
            id,
        },
        onError: (e) => toast.error(CleanMessage(e.message)),
        notifyOnNetworkStatusChange: true,
    });

    const Cancel = () => {
        setNewForm(false);
        scrollTop();
    };
    // Mutation
    const [CreateFunc, { loading: cLoading }] = useMutation(CREATE, {
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            const { message, doc } = d.NewCarePlan;
            toast.success(message);
            sendNotification(message, ACTION_TYPE.CREATE, EVENT_TARGET.CARE_PLAN);
            setNewForm(false);
            setItem(doc);
        },
    });

    function scrollTop() {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }

    const [confirmFunc, { loading: fLoading }] = useMutation(CONFIRM_PLAN, {
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            const { message, doc } = d.ConfirmCarePlan;
            toast.success(message);
            setNewForm(false);
            sendNotification(message, ACTION_TYPE.UPDATE, EVENT_TARGET.CARE_PLAN);
            setItem(doc);
        },
    });

    const [completePlanFunc, { loading: endLoading }] = useMutation(TERMINATE_PLAN, {
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            const { message, doc } = d.TerminateCarePlan;
            toast.success(message);
            setNewForm(false);
            setItem(doc);
            sendNotification(message, ACTION_TYPE.UPDATE, EVENT_TARGET.CARE_PLAN);
            setReview(false);
        },
    });

    const [replanFunc, { loading: reLoading }] = useMutation(REPLAN, {
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            const { message, doc } = d.RePlanCarePlan;
            toast.success(message);
            setNewForm(false);
            setItem(doc);
            sendNotification(message, ACTION_TYPE.UPDATE, EVENT_TARGET.CARE_PLAN);
        },
    });

    if (patient && !patient.lastAdmission)
        return (
            <>
                <div className="text-center">
                    <h2 className="text-pink">Feature can only be used for in-patient only!</h2>
                </div>
            </>
        );

    EventEmitter.subscribe(EVENT_TARGET.CARE_PLAN, () => refetch());

    return (
        <>
            <Helmet>
                <title>
                    {title} | {appName}
                </title>
            </Helmet>
            <div className="row justify-content-center">
                {!item && newForm && !review && (
                    <div className="col-12 fade-in">
                        <div className="card">
                            <div className="card-head">
                                <header>
                                    <button
                                        onClick={() => {
                                            setNewForm(false);
                                            setItem(null);
                                        }}
                                        title="Back"
                                        className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                    >
                                        <i className="material-icons text-primary">keyboard_backspace</i>
                                    </button>
                                    New Care Plan
                                </header>
                            </div>
                            <div className="card-body m-b-20">
                                <NewCarePlan
                                    onCancel={Cancel}
                                    onSubmit={async ({ model, confirmed }) => {
                                        await CreateFunc({
                                            variables: {
                                                model: {
                                                    ...model,
                                                    admission: patient.lastAdmission,
                                                    patient: id,
                                                },
                                                confirmed,
                                            },
                                        });
                                    }}
                                />
                                <LoadingSpinner loading={cLoading} />
                            </div>
                        </div>
                    </div>
                )}
                {!item && !newForm && !review && (
                    <>
                        <div className="col-12 fade-in">
                            <div className="card">
                                <div className="card-head">
                                    <header>{title}</header>
                                </div>
                                <div className="card-body" style={{ minHeight: "480px" }}>
                                    <LoadingSpinner loading={loading || fLoading} />
                                    {!loading && (
                                        <CarePlanList
                                            onView={(plan) => {
                                                setNewForm(false);
                                                setItem(plan);
                                                scrollTop();
                                            }}
                                            className="fade-in"
                                            items={data && data.GetCarePlans.docs}
                                            onConfirm={async (plan) => {
                                                await confirmFunc({ variables: { id: plan.id } });
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {item && !newForm && !review && (
                <div className="col-12 fade-in">
                    <div className="card">
                        <div className="card-head">
                            <header>
                                <button
                                    onClick={() => {
                                        setNewForm(false);
                                        setItem(null);
                                    }}
                                    title="Back"
                                    className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                >
                                    <i className="material-icons text-primary">keyboard_backspace</i>
                                </button>
                                {title} Detail
                            </header>
                        </div>
                        <div className="card-body" style={{ minHeight: "480px" }}>
                            <PlanDetail
                                onConfirm={async (plan) => {
                                    await confirmFunc({ variables: { id: plan.id } });
                                }}
                                item={item}
                                onComplete={async ({ item: plan, status }) => {
                                    if (window.confirm("Are sure you want to proceed?")) {
                                        await completePlanFunc({ variables: { id: plan.id, status } });
                                    }
                                }}
                                onRePlan={async (plan) => {
                                    if (window.confirm("Are sure you want to proceed?")) {
                                        await replanFunc({ variables: { id: plan.id } });
                                    }
                                }}
                                onReView={(plan) => {
                                    setItem(plan);
                                    setNewForm(false);
                                    setReview(true);
                                }}
                            />
                            <LoadingSpinner loading={fLoading || endLoading || reLoading} />
                        </div>
                    </div>
                </div>
            )}

            {!newForm && review && item && (
                <div className="col-12 fade-in">
                    <div className="card">
                        <div className="card-head">
                            <header>
                                <button
                                    onClick={() => {
                                        setNewForm(false);
                                        setItem(null);
                                        setReview(false);
                                    }}
                                    title="Back"
                                    className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                >
                                    <i className="material-icons text-primary">keyboard_backspace</i>
                                </button>
                                {title} Review
                            </header>
                        </div>
                        <div className="card-body" style={{ minHeight: "480px" }}>
                            <ReviewPlan
                                item={item}
                                onCancel={() => {
                                    setNewForm(false);
                                    setReview(false);
                                    scrollTop();
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {!newForm && (
                <FabButton
                    title="New CarePlan"
                    onClick={() => {
                        setNewForm(true);
                        setItem(null);
                        setReview(false);
                        scrollTop();
                    }}
                />
            )}
        </>
    );
};

export default CarePlan;
